import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/UnpublishedOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import PolicyIcon from '@mui/icons-material/PolicyOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ComplianceMessageService from '../services/complianceMessageService';
import { richTextContentSx } from './richTextStyles';
import { useAuth } from '../context/AuthContext';
import {
  currentPeriod,
  formatPeriod,
  hasSpanish,
  isBlankBody,
  isFuturePeriod,
  liveMessageId,
  nextPeriod,
  splitLinks,
  validateMessage,
  LINK_MAX,
  TITLE_MAX,
} from '../utils/complianceMessages';
import { isBlankHtml, plainTextToHtml, sanitizeComplianceHtml } from '../utils/complianceHtml';

// The editor (TipTap + ProseMirror, ~150 kB gzipped) is only needed in the edit dialog, so it is
// loaded on demand rather than added to the bundle every admin page downloads.
const RichTextEditor = lazy(() => import('./RichTextEditor'));

const EditorLoading = ({ label }) => (
  <Box aria-busy="true" aria-label={`${label} loading`}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{label}</Typography>
    <Skeleton variant="rounded" height={190} />
  </Box>
);

/**
 * DV-311: Amway compliance requires a monthly compliance message shown to IBOs. Admins publish
 * or replace each month's message here; members see the newest published month that is not in
 * the future, and every published month stays in their archive. Bilingual (EN + ES).
 */

const EMPTY_FORM = {
  period: '',
  titleEn: '',
  bodyEn: '',
  titleEs: '',
  bodyEs: '',
  linkUrl: '',
};

const BODY_HELP =
  'Formatting is supported: headings, bold, lists, links, colors and more. Pasted text keeps its bold, italics, lists and links.';

// Bodies are edited as HTML. Older plain-text (TEXT) bodies are converted when opened, and are
// saved as HTML from then on.
const bodyForEditor = (body, bodyFormat) => (bodyFormat === 'HTML' ? body || '' : plainTextToHtml(body));

const messageToForm = (m) => ({
  period: m.period || '',
  titleEn: m.titleEn || '',
  bodyEn: bodyForEditor(m.bodyEn, m.bodyFormat),
  titleEs: m.titleEs || '',
  bodyEs: bodyForEditor(m.bodyEs, m.bodyFormat),
  linkUrl: m.linkUrl || '',
});

// Optional fields go out as null rather than "" so the backend sees them as absent. Bodies are
// sanitized to the allowed HTML here too; the backend does the same on its side.
const formToPayload = (form) => ({
  period: form.period,
  titleEn: form.titleEn.trim(),
  titleEs: form.titleEs.trim() || null,
  bodyFormat: 'HTML',
  bodyEn: sanitizeComplianceHtml(form.bodyEn),
  bodyEs: isBlankHtml(form.bodyEs) ? null : sanitizeComplianceHtml(form.bodyEs),
  linkUrl: form.linkUrl.trim() || null,
});

const formatInstant = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const StatusChip = ({ status }) => (status === 'PUBLISHED'
  ? <Chip size="small" color="success" label="Published" />
  : <Chip size="small" label="Draft" />);

/** Plain text with bare http(s) URLs as links, built as React elements (never raw HTML). */
const LinkifiedText = ({ text }) => splitLinks(text).map((segment, i) => (segment.type === 'url' ? (
  <Link key={i} href={segment.value} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
    {segment.value}
  </Link>
) : (
  <React.Fragment key={i}>{segment.value}</React.Fragment>
)));

/**
 * A message body as members see it: HTML bodies sanitized to the allowed formatting, TEXT
 * bodies as plain text with their line breaks and bare URLs linked.
 */
const MessageBody = ({ body, bodyFormat }) => (bodyFormat === 'HTML' ? (
  <Typography
    variant="body2"
    component="div"
    sx={richTextContentSx}
    // Sanitized to the DV-311 allowlist (no scripts, handlers or unsafe links) right here.
    dangerouslySetInnerHTML={{ __html: sanitizeComplianceHtml(body) }}
  />
) : (
  <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
    <LinkifiedText text={body} />
  </Typography>
));

// The member dashboard labels the optional link in the member's language.
const READ_MORE = { English: 'Read more', Spanish: 'Leer más' };

/* ─── One language of the member-facing card ─────────────────────────── */
const PreviewCard = ({ language, period, title, body, bodyFormat, linkUrl }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }} data-testid={`preview-${language.toLowerCase()}`}>
    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
      {language} · {formatPeriod(period) || 'No month chosen'}
    </Typography>
    {title || !isBlankBody(body, bodyFormat) ? (
      <>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1, overflowWrap: 'anywhere' }}>
          {title}
        </Typography>
        <MessageBody body={body} bodyFormat={bodyFormat} />
        {linkUrl && (
          <Button
            variant="outlined"
            size="small"
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon fontSize="small" />}
            sx={{ mt: 2, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            {READ_MORE[language]}
          </Button>
        )}
      </>
    ) : (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        No {language} version yet. Both languages are required before publishing.
      </Typography>
    )}
  </Paper>
);

const PreviewDialog = ({ message, onClose }) => (
  <Dialog open={!!message} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ pb: 1 }}>Preview: what members will see</DialogTitle>
    <DialogContent dividers sx={{ p: 3 }}>
      {message && (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Members see the version in their app language: Spanish for Spanish speakers, English otherwise.
          </Typography>
          <PreviewCard
            language="English"
            period={message.period}
            title={message.titleEn}
            body={message.bodyEn}
            bodyFormat={message.bodyFormat}
            linkUrl={message.linkUrl}
          />
          <PreviewCard
            language="Spanish"
            period={message.period}
            title={message.titleEs}
            body={message.bodyEs}
            bodyFormat={message.bodyFormat}
            linkUrl={message.linkUrl}
          />
        </Stack>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 2 }}>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

/* ═══════════════════════════════════════════════════════════════════════ */
const ComplianceMessagesPage = () => {
  const { getAccessToken } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Create / edit dialog. `editing` is the message being edited, null when creating.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [preview, setPreview] = useState(null);
  // { kind: 'publish' | 'unpublish' | 'delete', message }
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  // Draft that was asked to publish without its Spanish version
  const [missingSpanish, setMissingSpanish] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await ComplianceMessageService.list(getAccessToken);
      // The backend already sorts newest month first; sorting again keeps the table right regardless.
      setMessages([...(data || [])].sort((a, b) => (a.period < b.period ? 1 : -1)));
    } catch (e) {
      setLoadError(e.message || 'Failed to load compliance messages');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const liveId = liveMessageId(messages);
  const editingPublished = editing?.status === 'PUBLISHED';

  /* ── dialog ────────────────────────────────────────────────────────── */
  const openCreate = () => {
    // Suggest the first month from the current one that has no message yet.
    const taken = new Set(messages.map((m) => m.period));
    let period = currentPeriod();
    while (taken.has(period)) period = nextPeriod(period);
    setEditing(null);
    setForm({ ...EMPTY_FORM, period });
    setFormErrors({});
    setSaveError(null);
    setDialogOpen(true);
  };

  const openEdit = (message) => {
    setEditing(message);
    setForm(messageToForm(message));
    setFormErrors({});
    setSaveError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleChange = (field) => (e) => setField(field, e.target.value);

  const handleSave = async () => {
    const errors = validateMessage(form, { requireSpanish: editingPublished });
    const clash = messages.find((m) => m.period === form.period && m.id !== editing?.id);
    if (clash && !errors.period) {
      errors.period = `${formatPeriod(form.period)} already has a message. Edit that one instead.`;
    }
    if (Object.values(errors).some(Boolean)) {
      setFormErrors(errors);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const payload = formToPayload(form);
      if (editing) {
        await ComplianceMessageService.update(getAccessToken, editing.id, payload);
        notify(editingPublished
          ? `${formatPeriod(form.period)} updated. Members see the new text now.`
          : `Draft for ${formatPeriod(form.period)} saved`);
      } else {
        await ComplianceMessageService.create(getAccessToken, payload);
        notify(`Draft for ${formatPeriod(form.period)} created. Publish it when it is ready.`);
      }
      setDialogOpen(false);
      loadMessages();
    } catch (e) {
      setSaveError(e.message || 'Failed to save the message');
    } finally {
      setSaving(false);
    }
  };

  /* ── publish / unpublish / delete ──────────────────────────────────── */
  const askPublish = (message) => {
    if (!hasSpanish(message)) {
      setMissingSpanish(message);
      return;
    }
    setConfirm({ kind: 'publish', message });
  };

  const runConfirmed = async () => {
    const { kind, message } = confirm;
    const month = formatPeriod(message.period);
    setConfirmBusy(true);
    try {
      if (kind === 'publish') {
        await ComplianceMessageService.publish(getAccessToken, message.id);
        notify(`${month} published`);
      } else if (kind === 'unpublish') {
        await ComplianceMessageService.unpublish(getAccessToken, message.id);
        notify(`${month} unpublished. It is a draft again.`);
      } else {
        await ComplianceMessageService.remove(getAccessToken, message.id);
        notify(`Draft for ${month} deleted`);
      }
      setConfirm(null);
      loadMessages();
    } catch (e) {
      notify(e.message || `Failed to ${kind} the message`, 'error');
      setConfirm(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const publishImpact = (message) => {
    const month = formatPeriod(message.period);
    if (isFuturePeriod(message.period)) {
      return `Members will see it from ${month} onwards. Until then it stays hidden from them.`;
    }
    const live = messages.find((m) => m.id === liveId);
    if (!live || message.period > live.period) {
      return 'Members will see it right away as the current compliance message.';
    }
    return `It will be added to members' archive. They keep seeing ${formatPeriod(live.period)} as the current message.`;
  };

  const confirmCopy = confirm && {
    publish: {
      title: `Publish ${formatPeriod(confirm.message.period)}?`,
      body: publishImpact(confirm.message),
      action: 'Publish',
      color: 'primary',
    },
    unpublish: {
      title: `Unpublish ${formatPeriod(confirm.message.period)}?`,
      body: confirm.message.id === liveId
        ? 'Members will stop seeing this message, and it will leave their archive. They will see the previous published month instead, if there is one.'
        : 'Members will stop seeing this message, and it will leave their archive. It becomes a draft again.',
      action: 'Unpublish',
      color: 'warning',
    },
    delete: {
      title: `Delete the draft for ${formatPeriod(confirm.message.period)}?`,
      body: `"${confirm.message.titleEn}" will be deleted. This cannot be undone.`,
      action: 'Delete',
      color: 'error',
    },
  }[confirm.kind];

  /* ── render ────────────────────────────────────────────────────────── */
  return (
    <Box>
      {/* ── page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PolicyIcon sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>Compliance Messages</Typography>
            <Typography variant="body2" color="text.secondary">
              One message per month. Members see the newest published month that isn't in the future
              (Pacific time), and every published month stays in their archive.
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Message
        </Button>
      </Box>

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={loadMessages}>Retry</Button>}
        >
          {loadError}
        </Alert>
      )}

      {/* ── messages table ── */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell>Title (English)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {loadError ? 'Messages could not be loaded.' : 'No compliance messages yet. Create the first one.'}
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((m) => {
                  const month = formatPeriod(m.period);
                  const isPublished = m.status === 'PUBLISHED';
                  const isFuture = isFuturePeriod(m.period);
                  return (
                    <TableRow key={m.id} hover data-testid={`message-row-${m.id}`}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{month}</Typography>
                        {isPublished && isFuture && (
                          <Typography variant="caption" display="block" color="info.main">Visible from {month}</Typography>
                        )}
                        {m.id === liveId && (
                          <Typography variant="caption" display="block" color="success.main">Members see this now</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" noWrap>{m.titleEn}</Typography>
                        {!hasSpanish(m) && (
                          <Typography variant="caption" display="block" color="warning.main">Spanish version missing</Typography>
                        )}
                      </TableCell>
                      <TableCell><StatusChip status={m.status} /></TableCell>
                      <TableCell>
                        <Typography variant="body2">{m.updatedBy || m.createdBy || '-'}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatInstant(m.updatedAt || m.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Preview">
                          <IconButton size="small" aria-label={`Preview ${month}`} onClick={() => setPreview(m)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" aria-label={`Edit ${month}`} onClick={() => openEdit(m)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isPublished ? (
                          <Tooltip title="Unpublish">
                            <IconButton
                              size="small"
                              color="warning"
                              aria-label={`Unpublish ${month}`}
                              onClick={() => setConfirm({ kind: 'unpublish', message: m })}
                            >
                              <UnpublishedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <>
                            <Tooltip title="Publish">
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={`Publish ${month}`}
                                onClick={() => askPublish(m)}
                              >
                                <PublishIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete draft">
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={`Delete ${month}`}
                                onClick={() => setConfirm({ kind: 'delete', message: m })}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ══════════════════ Create / edit dialog ══════════════════════════ */}
      <Dialog
        open={dialogOpen}
        // Escape or a stray click outside would throw away a formatted message, so only Cancel closes the editor
        onClose={(event, reason) => {
          if (reason !== 'escapeKeyDown' && reason !== 'backdropClick') closeDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editing ? `Edit ${formatPeriod(editing.period)}` : 'New Compliance Message'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            {editingPublished && (
              <Alert severity="warning">
                This message is published. Saving changes what members see immediately, and both
                languages are required.
              </Alert>
            )}
            {saveError && <Alert severity="error">{saveError}</Alert>}

            <TextField
              label="Month"
              type="month"
              required
              value={form.period}
              onChange={handleChange('period')}
              error={!!formErrors.period}
              helperText={formErrors.period || 'The calendar month this message is for.'}
              InputLabelProps={{ shrink: true }}
              inputProps={{ placeholder: 'YYYY-MM' }}
              sx={{ maxWidth: 260 }}
            />

            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1, mb: -1 }}>
              English
            </Typography>
            <TextField
              label="Title (English)"
              required
              fullWidth
              value={form.titleEn}
              onChange={handleChange('titleEn')}
              error={!!formErrors.titleEn}
              helperText={formErrors.titleEn}
              inputProps={{ maxLength: TITLE_MAX }}
            />
            <Suspense fallback={<EditorLoading label="Message (English)" />}>
              <RichTextEditor
                label="Message (English)"
                required
                value={form.bodyEn}
                onChange={(html) => setField('bodyEn', html)}
                error={!!formErrors.bodyEn}
                helperText={formErrors.bodyEn || BODY_HELP}
              />
            </Suspense>

            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1, mb: -1 }}>
              Spanish {editingPublished ? '' : '(can be added later, required to publish)'}
            </Typography>
            <TextField
              label="Title (Spanish)"
              required={editingPublished}
              fullWidth
              value={form.titleEs}
              onChange={handleChange('titleEs')}
              error={!!formErrors.titleEs}
              helperText={formErrors.titleEs}
              inputProps={{ maxLength: TITLE_MAX }}
            />
            <Suspense fallback={<EditorLoading label="Message (Spanish)" />}>
              <RichTextEditor
                label="Message (Spanish)"
                required={editingPublished}
                value={form.bodyEs}
                onChange={(html) => setField('bodyEs', html)}
                error={!!formErrors.bodyEs}
                helperText={formErrors.bodyEs || BODY_HELP}
              />
            </Suspense>

            <TextField
              label="Link URL (optional)"
              fullWidth
              placeholder="https://..."
              value={form.linkUrl}
              onChange={handleChange('linkUrl')}
              error={!!formErrors.linkUrl}
              helperText={formErrors.linkUrl || 'Shown under the message in both languages. Must start with http:// or https://.'}
              inputProps={{ maxLength: LINK_MAX }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            startIcon={<VisibilityIcon />}
            onClick={() => setPreview({ ...form, bodyFormat: 'HTML' })}
            sx={{ mr: 'auto' }}
          >
            Preview
          </Button>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (editing ? 'Save' : 'Create Draft')}
          </Button>
        </DialogActions>
      </Dialog>

      <PreviewDialog message={preview} onClose={() => setPreview(null)} />

      {/* ══════════════════ Publish / unpublish / delete confirm ══════════ */}
      <Dialog open={!!confirm} onClose={() => !confirmBusy && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{confirmCopy?.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmCopy?.body}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirm(null)} disabled={confirmBusy}>Cancel</Button>
          <Button variant="contained" color={confirmCopy?.color} onClick={runConfirmed} disabled={confirmBusy}>
            {confirmBusy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : confirmCopy?.action}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════ Publish blocked: no Spanish ═══════════════════ */}
      <Dialog open={!!missingSpanish} onClose={() => setMissingSpanish(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Spanish version needed</DialogTitle>
        <DialogContent>
          <Typography>
            {missingSpanish && formatPeriod(missingSpanish.period)} can't be published yet: a published
            message must have both the Spanish title and the Spanish message, because most members read it
            in Spanish. Add them, then publish.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setMissingSpanish(null)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              const message = missingSpanish;
              setMissingSpanish(null);
              openEdit(message);
            }}
          >
            Add Spanish
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ComplianceMessagesPage;
